/**
 * Column mapping configuration for campaign import
 * Maps Excel column names to JSON property names
 */
const CAMPAIGN_COLUMN_MAPPING = {
  omni_guid: ['omni_guid', 'Omni GUID', 'omni guid'],
  match_status: ['match_status', 'Match Status', 'match status'],
  planned_campaign_id: ['Planned Campaign', 'planned_campaign_id', 'Planned Campaign ID'],
  ordered_campaign_id: ['Orphan Campaign', 'ordered_campaign_id', 'Ordered Campaign ID']
};

/**
 * Gets the value from a row using multiple possible column names
 * @param {Object} row - The row object from parsed Excel
 * @param {Array<string>} possibleNames - Array of possible column names to check
 * @param {string} defaultValue - Default value if no column is found
 * @returns {string} - The found value or default
 */
const getValueFromRow = (row, possibleNames, defaultValue = '') => {
  for (const name of possibleNames) {
    if (row[name] !== undefined && row[name] !== null) {
      return row[name];
    }
  }
  return defaultValue;
};

/**
 * Parses an Excel or CSV file and transforms it to campaign mapping format
 * @param {File} file - The Excel/CSV file to parse
 * @param {string} defaultOmniGuid - Default omni_guid to use if not found in file (e.g., clientId)
 * @returns {Promise<Array>} - Promise resolving to array of campaign mapping objects
 */
export const parseCampaignExcelFile = (file, defaultOmniGuid = '') => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const XLSX = await import('xlsx');
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Transform to required format using column mapping
        const transformedData = jsonData.map(row => ({
          omni_guid: getValueFromRow(row, CAMPAIGN_COLUMN_MAPPING.omni_guid, defaultOmniGuid),
          match_status: getValueFromRow(row, CAMPAIGN_COLUMN_MAPPING.match_status),
          planned_campaign_id: getValueFromRow(row, CAMPAIGN_COLUMN_MAPPING.planned_campaign_id),
          ordered_campaign_id: getValueFromRow(row, CAMPAIGN_COLUMN_MAPPING.ordered_campaign_id)
        }));
        
        resolve(transformedData);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Validates if a file is a valid Excel or CSV file
 * @param {File} file - The file to validate
 * @returns {boolean} - True if file is valid
 */
export const isValidExcelFile = (file) => {
  if (!file) return false;
  
  const validTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls
    'text/csv' // .csv
  ];
  
  const validExtensions = ['.xlsx', '.xls', '.csv'];
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  
  return validTypes.includes(file.type) || validExtensions.includes(fileExtension);
};

